import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { adminGet, adminPut, reportError } from './adminApi';

const EMPTY = { rating: 0, count: 0, url: '', enabled: false, reviews: [] };
const EMPTY_REVIEW = { author: '', rating: 5, text: '', date: '' };
const reviewKey = (r) => `${r.author}|${r.date}|${(r.text || '').slice(0, 30)}`;

export function AdminGoogleReviewsCard({ adminKey }) {
  const [data, setData] = useState(EMPTY);
  const [draft, setDraft] = useState(EMPTY_REVIEW);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGet('/api/admin/google-reviews', adminKey);
        if (!cancelled) setData({ ...EMPTY, ...res.data, enabled: !!res.data.enabled, reviews: res.data.reviews || [] });
      } catch (err) {
        reportError('avis Google', err);
      }
    })();
    return () => { cancelled = true; };
  }, [adminKey]);

  const save = async (override) => {
    const payload = override || data;
    try {
      await adminPut('/api/admin/google-reviews', adminKey, {}, {
        ...payload,
        rating: Number(payload.rating) || 0,
        count: Number(payload.count) || 0,
        reviews: (payload.reviews || []).map((r) => ({ ...r, rating: Number(r.rating) || 5 })),
      });
      toast.success('Avis Google enregistrés');
    } catch (err) {
      reportError('enregistrement avis Google', err, (m) => toast.error(m || "Erreur lors de l'enregistrement"));
    }
  };

  const addReview = () => {
    if (!draft.author.trim() || !draft.text.trim()) { toast.error('Auteur et texte obligatoires'); return; }
    const next = { ...data, reviews: [...data.reviews, { ...draft, rating: Number(draft.rating) || 5 }] };
    setData(next);
    setDraft(EMPTY_REVIEW);
    save(next);
  };

  const removeReview = (idx) => {
    const next = { ...data, reviews: data.reviews.filter((_, i) => i !== idx) };
    setData(next);
    save(next);
  };

  return (
    <Card className="p-5" data-testid="admin-google-reviews-card">
      <h3 className="font-['Space_Grotesk'] font-semibold mb-2">Avis Google (affichés sur l'accueil)</h3>
      <p className="text-xs text-muted-foreground mb-4">Saisissez votre note globale, le nombre d'avis et le lien vers votre fiche Google. Ajoutez jusqu'à 3 avis mis en avant (les 3 premiers sont affichés).</p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="text-xs font-medium block mb-1">Note globale (0-5)</label>
          <Input className="h-9" type="number" step="0.1" min="0" max="5" value={data.rating} onChange={(e) => setData((p) => ({ ...p, rating: e.target.value }))} data-testid="google-rating-input" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Nombre d'avis</label>
          <Input className="h-9" type="number" min="0" value={data.count} onChange={(e) => setData((p) => ({ ...p, count: e.target.value }))} data-testid="google-count-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium block mb-1">Lien "Voir tous nos avis Google"</label>
          <Input className="h-9" value={data.url} onChange={(e) => setData((p) => ({ ...p, url: e.target.value }))} placeholder="https://g.page/r/.../review ou lien Google Maps" data-testid="google-url-input" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
        <Switch checked={data.enabled} onCheckedChange={(v) => setData((p) => ({ ...p, enabled: v }))} data-testid="google-enabled-switch" />
        Afficher la section Avis Google sur la page d'accueil
      </label>
      <Button size="sm" onClick={() => save()} data-testid="google-save-btn">Enregistrer</Button>

      <div className="mt-6 pt-4 border-t border-border">
        <h4 className="text-sm font-semibold mb-3">Avis mis en avant ({data.reviews.length})</h4>
        {data.reviews.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="google-reviews-admin-list">
            {data.reviews.map((r, i) => (
              <div key={reviewKey(r)} className="flex items-start gap-3 text-sm p-3 rounded-lg bg-[hsl(var(--secondary))]/60">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{r.author}</span>
                    <span className="text-amber-500 text-xs">{'★'.repeat(Number(r.rating) || 0)}</span>
                    {r.date && <span className="text-xs text-muted-foreground">{r.date}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.text}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeReview(i)} aria-label="Supprimer" data-testid="google-review-delete-btn"><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Input className="h-9" placeholder="Prénom / Nom" value={draft.author} onChange={(e) => setDraft((p) => ({ ...p, author: e.target.value }))} data-testid="google-review-author-input" />
          <Select value={String(draft.rating)} onValueChange={(v) => setDraft((p) => ({ ...p, rating: Number(v) }))}>
            <SelectTrigger className="h-9" data-testid="google-review-rating-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} étoile{n > 1 ? 's' : ''}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="h-9" placeholder="Date (ex: mars 2026)" value={draft.date} onChange={(e) => setDraft((p) => ({ ...p, date: e.target.value }))} data-testid="google-review-date-input" />
          <Button size="sm" className="h-9" onClick={addReview} data-testid="google-review-add-btn">Ajouter l'avis</Button>
          <Textarea className="sm:col-span-4" rows={2} placeholder="Texte de l'avis" value={draft.text} onChange={(e) => setDraft((p) => ({ ...p, text: e.target.value }))} data-testid="google-review-text-input" />
        </div>
      </div>
    </Card>
  );
}
